import { useTranslation } from "react-i18next";
import type { BattleReward } from "../data/battleApi";
import type { BattlePhase } from "../store/battleStore";

interface BattleResultProps {
  phase: BattlePhase;
  playerName: string;
  onRestart: () => void;
  onLeave?: () => void;
  loading?: boolean;
  reward?: BattleReward | null;
}

export function BattleResult({
  phase,
  playerName,
  onRestart,
  onLeave,
  loading = false,
  reward
}: BattleResultProps) {
  const { t } = useTranslation();

  if (phase === "playing") {
    return null;
  }

  const won = phase === "won";
  const isMaxLevel = reward?.heroLevelAfter === 100 || reward?.xpForNextLevel === 0;
  const progressPercent = reward
    ? isMaxLevel
      ? 100
      : Math.min(100, (reward.xpIntoLevel / reward.xpForNextLevel) * 100)
    : 0;

  return (
    <div className="battle-result" role="dialog" aria-modal="true" aria-labelledby="battle-result-title">
      <div className={`battle-result__card battle-result__card--${phase}`}>
        <span className="battle-result__icon" aria-hidden="true">{won ? "★" : "↻"}</span>
        <h2 id="battle-result-title">{t(won ? "victoryTitle" : "defeatTitle")}</h2>
        <p>{t(won ? "victoryBody" : "defeatBody", { playerName })}</p>
        {loading ? <div className="reward-loading">{t("rewardCalculating")}</div> : null}
        {reward ? (
          <div className="battle-rewards">
            {reward.levelUp ? (
              <div className="level-up-burst">
                <span aria-hidden="true">★</span>
                <strong>{t("levelUp", { level: reward.heroLevelAfter })}</strong>
              </div>
            ) : null}
            {reward.firstCompletion ? (
              <div className="campaign-unlock-burst">
                <span aria-hidden="true">◆</span>
                <strong>{reward.campaignCompleted
                  ? t("campaignComplete")
                  : t("campaignLevelUnlocked", { level: reward.campaignLevelUnlocked })}</strong>
              </div>
            ) : null}
            {reward.isReplay && reward.status === "victory" ? (
              <p className="replay-reward">{t("replayReward")}</p>
            ) : null}
            <div className="reward-grid">
              <span><strong>+{reward.xpEarned}</strong><small>XP</small></span>
              <span><strong>+{reward.coinsEarned}</strong><small>{t("coins")}</small></span>
              <span>
                <strong>{reward.correctAnswers}/{reward.totalAnswers}</strong>
                <small>{t("correctAnswers")}</small>
              </span>
              <span><strong>×{reward.maxCombo}</strong><small>COMBO</small></span>
            </div>
            <div className="reward-progress">
              <div>
                <strong>{t("heroLevel", { level: reward.heroLevelAfter })}</strong>
                <small>{isMaxLevel
                  ? t("maxHeroLevel")
                  : `${reward.xpIntoLevel}/${reward.xpForNextLevel} XP`}</small>
              </div>
              <span><i style={{ width: `${progressPercent}%` }} /></span>
            </div>
            <p className="reward-streak">
              🔥 {t("winStreak", { count: reward.currentStreak })}
              {reward.upgradePointsEarned > 0
                ? ` · ${t("earnedUpgradePoints", { count: reward.upgradePointsEarned })}`
                : ""}
            </p>
          </div>
        ) : null}
        <div className="battle-result__actions">
          <button type="button" disabled={loading} onClick={onRestart}>{t("playAgain")}</button>
          {onLeave ? (
            <button className="battle-result__secondary" type="button" onClick={onLeave}>
              {t("backToLobby")}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
